/**
 * Paywall funnel analytics — logs to our own backend, not GA or PostHog.
 *
 * Security model:
 *   - Every call is authenticated with the user's Supabase JWT.
 *   - The backend validates the JWT and only accepts events in its allowlist.
 *   - The backend sets created_at server-side; the frontend never supplies a timestamp.
 *   - This function is always fire-and-forget: it will NEVER throw or block a user action.
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

/**
 * Returns 'mobile' | 'tablet' | 'desktop' from the user agent.
 */
const getDeviceType = () => {
  const ua = navigator.userAgent || '';
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};

/**
 * Log a single paywall funnel event to the backend.
 *
 * @param {string} eventType  - Must match ALLOWED_EVENTS on the backend.
 * @param {object} metadata   - Any serialisable key/value pairs. Never include PII.
 */
export async function logPaywallEvent(eventType, metadata = {}) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return; // not authenticated — skip silently

    // Fire and don't await response in a way that blocks UI
    fetch(`${BACKEND_URL}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ event_type: eventType, metadata }),
    }).catch(() => {
      // Network failure — silently discard, never propagate
    });
  } catch {
    // Auth or serialisation error — silently discard
  }
}

export { getDeviceType };
